import { useState, useEffect, useCallback } from 'react'
import {
    Search, Package, Eye, PlusCircle, Pencil, Trash2,
    ToggleLeft, ShieldCheck, Building2, UserCog, KeyRound,
    Lock, ShieldAlert, Activity, AlertTriangle, Info, X,
    ChevronDown, ChevronUp, Calendar, Settings2,
} from 'lucide-react'
import { toast } from 'sonner'

// Componentes UI Globales
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog'

// Servicio
import { activityLogService, ActivityLog } from '../services/activity-log.service'

// ─── Tipos ─────────────────────────────────────────────────────────────────────

interface PaginationState {
    currentPage: number
    total: number
    perPage: number
    lastPage: number
    from: number
    to: number
}

// ─── Configuración de eventos ─────────────────────────────────────────────────

interface EventConfig {
    label: string
    bgColor: string
    textColor: string
    Icon: React.ElementType
    severity: 'info' | 'warning' | 'critical'
}

const EVENT_CONFIG: Record<string, EventConfig> = {
    created: { label: 'Creación', bgColor: 'bg-emerald-100', textColor: 'text-emerald-700', Icon: PlusCircle, severity: 'info' },
    updated: { label: 'Edición', bgColor: 'bg-blue-100', textColor: 'text-blue-700', Icon: Pencil, severity: 'info' },
    deleted: { label: 'Eliminación', bgColor: 'bg-red-100', textColor: 'text-red-700', Icon: Trash2, severity: 'warning' },
    status_changed: { label: 'Cambio de estado', bgColor: 'bg-amber-100', textColor: 'text-amber-700', Icon: ToggleLeft, severity: 'info' },
    role_permissions_synced: { label: 'Permisos', bgColor: 'bg-violet-100', textColor: 'text-violet-700', Icon: ShieldCheck, severity: 'warning' },
    user_sucursals_synced: { label: 'Sucursales', bgColor: 'bg-cyan-100', textColor: 'text-cyan-700', Icon: Building2, severity: 'info' },
    role_assigned: { label: 'Rol asignado', bgColor: 'bg-purple-100', textColor: 'text-purple-700', Icon: UserCog, severity: 'warning' },
    password_reset: { label: 'Cambio de contraseña', bgColor: 'bg-rose-100', textColor: 'text-rose-700', Icon: KeyRound, severity: 'warning' },
    account_locked: { label: 'Cuenta bloqueada', bgColor: 'bg-red-200', textColor: 'text-red-800', Icon: Lock, severity: 'critical' },
    unauthorized_access: { label: 'Acceso denegado', bgColor: 'bg-red-200', textColor: 'text-red-800', Icon: ShieldAlert, severity: 'critical' },
    setting_updated: { label: 'Configuracion', bgColor: 'bg-teal-100', textColor: 'text-teal-700', Icon: Activity, severity: 'info' },
    product_stock_manually_adjusted: { label: 'Ajuste de Stock', bgColor: 'bg-red-100', textColor: 'text-red-700', Icon: Settings2, severity: 'warning' },
}

const DEFAULT_EVENT: EventConfig = {
    label: 'Acción', bgColor: 'bg-slate-100', textColor: 'text-slate-700', Icon: Activity, severity: 'info',
}

const getEventConfig = (event: string): EventConfig =>
    EVENT_CONFIG[event] ?? DEFAULT_EVENT

// ─── Módulos ────────────────────────────────

const MODULE_LABELS: Record<string, string> = {
    User: 'Usuarios',
    Role: 'Roles',
    Sucursal: 'Sucursales',
    Product: 'Inventario',
    Sale: 'Ventas',
    Purchase: 'Compras',
    Category: 'Categorías',
    Permission: 'Permisos',
    Company: 'Empresa',
    ProductType: 'Tipos de producto',
    ProductStock: 'Stock de Producto'
}

const getModuleLabel = (subjectType: string | null): string =>
    subjectType ? (MODULE_LABELS[subjectType] ?? subjectType) : '—'

// ─── Severidad badge ──────────────────────────────────────────────────────────

const SEVERITY_CONFIG = {
    info: { label: '', Icon: Info, className: '' },
    warning: { label: 'Revisar', Icon: AlertTriangle, className: 'bg-amber-100 text-amber-700 border-amber-200' },
    critical: { label: 'Crítico', Icon: ShieldAlert, className: 'bg-red-100 text-red-700 border-red-200 animate-pulse' },
}

// ─── Nombres de campos de DB ─────────────────────────────────────────

const FIELD_LABELS: Record<string, string> = {
    name: 'Nombre',
    last_name: 'Apellidos',
    email: 'Correo',
    phone_number: 'Teléfono',
    is_active: 'Activo',
    role_id: 'Rol',
    company_id: 'Empresa',
    sucursal_id: 'Sucursal',
    use_pin_login: 'Login por PIN',
    access_all_sucursals: 'Todas las sucursales',
    sku: 'SKU',
    price: 'Precio',
    cost: 'Costo',
    stock: 'Stock',
    description: 'Descripción',
    address: 'Dirección',
    category_id: 'Categoría',
    created_at: 'Fecha de creación',
    updated_at: 'Fecha de actualización',
    deleted_at: 'Fecha de eliminación',
    password: 'Password',
    pin: 'PIN'
}

const getFieldLabel = (field: string): string => FIELD_LABELS[field] ?? field


// ─── Formatear fecha ──────────────────────────────────────────────────────────

function formatDate(raw: string): string {
    try {
        const d = new Date(raw)
        const now = new Date()
        const isToday = d.toDateString() === now.toDateString()
        const yesterday = new Date(now)
        yesterday.setDate(now.getDate() - 1)
        const isYesterday = d.toDateString() === yesterday.toDateString()

        const time = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })

        if (isToday) return `Hoy · ${time}`
        if (isYesterday) return `Ayer · ${time}`

        return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) + ` · ${time}`
    } catch {
        return raw
    }
}

// ─── Mensaje legible ──────────────────────────────────────────────────────────

function buildMessage(log: ActivityLog): string {
    const module = getModuleLabel(log.subject_type)
    const target = log.subject_name ? `"${log.subject_name}"` : ''

    const tpl: Record<string, string> = {
        created: `Creó ${target} en ${module}`,
        updated: `Modificó ${target} en ${module}`,
        deleted: `Eliminó ${target} de ${module}`,
        status_changed: `Cambió el estado de ${target || module}`,
        role_permissions_synced: `Actualizó los permisos del rol ${target}`,
        user_sucursals_synced: `Actualizó las sucursales de ${target}`,
        role_assigned: `Asignó el rol ${target}`,
        password_reset: `Cambió la contraseña de ${target || 'un usuario'}`,
        account_locked: `Bloqueó la cuenta de ${target}`,
    }

    return tpl[log.event] ?? log.description ?? `Realizó una acción en ${module}`
}

// ─── Componente: Modal de Detalle ─────────────────────────────────────────────

interface DetailModalProps {
    log: ActivityLog | null
    onClose: () => void
}

function ActivityDetailModal({ log, onClose }: DetailModalProps) {
    if (!log) return null

    const eventCfg = getEventConfig(log.event)
    const hasChanges = log.changes && Object.keys(log.changes).length > 0

    // Filtrar properties planas excluyendo 'attributes' y 'old' (que se procesan en 'changes')
    const extraProperties = log.properties ? Object.entries(log.properties).filter(([key]) => key !== 'attributes' && key !== 'old') : []
    const hasExtraProperties = extraProperties.length > 0

    // Renderizar valor de cambio de forma legible
    const renderValue = (field: string, value: unknown): string => {
        if (value === null || value === undefined || value === '') return '—'

        const isBooleanField = ['is_active', 'use_pin_login', 'access_all_sucursals'].includes(field)
        if (typeof value === 'boolean' || (isBooleanField && (value === 1 || value === 0 || value === '1' || value === '0'))) {
            if (value === true || value === 1 || value === '1') return 'Sí'
            if (value === false || value === 0 || value === '0') return 'No'
        }

        if (field === 'password' || field === 'pin') return '********'

        if (typeof value === 'object') return JSON.stringify(value)
        return String(value)
    }

    return (
        <Dialog open={!!log} onOpenChange={onClose}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <eventCfg.Icon className="h-5 w-5" />
                        Detalle de acción
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Información General */}
                    <section className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Información General
                        </h3>
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                            <div>
                                <dt className="text-slate-500">Usuario</dt>
                                <dd className="font-medium text-slate-900">{log.causer_name || 'Sistema'}</dd>
                            </div>
                            <div>
                                <dt className="text-slate-500">Email</dt>
                                <dd className="text-slate-700">{log.causer_email || '—'}</dd>
                            </div>
                            <div>
                                <dt className="text-slate-500">Acción</dt>
                                <dd>
                                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${eventCfg.bgColor} ${eventCfg.textColor}`}>
                                        <eventCfg.Icon className="h-3 w-3" />
                                        {eventCfg.label}
                                    </span>
                                </dd>
                            </div>
                            <div>
                                <dt className="text-slate-500">Módulo</dt>
                                <dd className="font-medium text-slate-900">{getModuleLabel(log.subject_type)}</dd>
                            </div>
                            <div>
                                <dt className="text-slate-500">Sucursal</dt>
                                <dd className="text-slate-700">{log.sucursal_name || '—'}</dd>
                            </div>
                            <div>
                                <dt className="text-slate-500">Fecha</dt>
                                <dd className="text-slate-700">{formatDate(log.created_at)}</dd>
                            </div>
                        </dl>
                    </section>

                    {/* Registro Afectado */}
                    {(log.subject_type || log.subject_name || log.subject_id) && (
                        <section className="rounded-lg border border-slate-200 bg-white p-4">
                            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                Registro Afectado
                            </h3>
                            <dl className="grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
                                <div>
                                    <dt className="text-slate-500">Tipo</dt>
                                    <dd className="font-medium text-slate-900">{getModuleLabel(log.subject_type)}</dd>
                                </div>
                                {log.subject_id && (
                                    <div>
                                        <dt className="text-slate-500">ID</dt>
                                        <dd className="font-mono text-slate-700">#{log.subject_id}</dd>
                                    </div>
                                )}
                                {log.subject_name && (
                                    <div>
                                        <dt className="text-slate-500">Nombre</dt>
                                        <dd className="font-medium text-slate-900">{log.subject_name}</dd>
                                    </div>
                                )}
                            </dl>
                        </section>
                    )}

                    {/* Cambios realizados */}
                    {hasChanges && (
                        <section className="rounded-lg border border-slate-200 bg-white p-4">
                            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                Cambios Realizados
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-slate-100">
                                            <th className="pb-2 text-left font-medium text-slate-500">Campo</th>
                                            <th className="pb-2 text-left font-medium text-slate-500">Antes</th>
                                            <th className="pb-2 text-left font-medium text-slate-500">Después</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {Object.entries(log.changes!).map(([field, change]) => (
                                            <tr key={field}>
                                                <td className="py-2 pr-4 font-mono text-xs text-slate-600">
                                                    {getFieldLabel(field)}
                                                </td>
                                                <td className="py-2 pr-4">
                                                    <span className="rounded bg-red-50 px-1.5 py-0.5 font-mono text-xs text-red-700">
                                                        {renderValue(field, change.old)}
                                                    </span>
                                                </td>
                                                <td className="py-2">
                                                    <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-mono text-xs text-emerald-700">
                                                        {renderValue(field, change.new)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* Detalles Adicionales */}
                    {hasExtraProperties && (
                        <section className="rounded-lg border border-slate-200 bg-white p-4">
                            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                Detalles Adicionales
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-slate-100">
                                            <th className="pb-2 text-left font-medium text-slate-500">Dato</th>
                                            <th className="pb-2 text-left font-medium text-slate-500">Valor</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {extraProperties.map(([field, value]) => (
                                            <tr key={field}>
                                                <td className="py-2 pr-4 font-mono text-xs text-slate-600">
                                                    {getFieldLabel(field)}
                                                </td>
                                                <td className="py-2 text-slate-700">
                                                    {renderValue(field, value)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* Contexto técnico */}
                    <section className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                            Contexto
                        </h3>
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500">
                            <div>
                                <dt className="text-slate-400">ID del log del sistema</dt>
                                <dd className="font-mono">#{log.id}</dd>
                            </div>
                            {log.causer_id && (
                                <div>
                                    <dt className="text-slate-400">ID del usuario</dt>
                                    <dd className="font-mono">#{log.causer_id}</dd>
                                </div>
                            )}
                        </dl>
                    </section>
                </div>
            </DialogContent>
        </Dialog>
    )
}

// ─── Componente Principal ─────────────────────────────────────────────────────

const INITIAL_PAGINATION: PaginationState = {
    currentPage: 1,
    total: 0,
    perPage: 10,
    lastPage: 1,
    from: 0,
    to: 0,
}

export function LogsAcciones() {
    // ─── Estado ───
    const [logs, setLogs] = useState<ActivityLog[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [pagination, setPagination] = useState<PaginationState>(INITIAL_PAGINATION)
    const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null)

    // Filtros
    const [searchQuery, setSearchQuery] = useState('')
    const [filterEvent, setFilterEvent] = useState('')
    const [filterModule, setFilterModule] = useState('')
    const [showAllSucursals, setShowAllSucursals] = useState(false)

    // ─── Fetch desde backend (paginación real) ───
    const fetchLogs = useCallback(async (page = 1) => {
        setIsLoading(true)
        try {
            const res = await activityLogService.getLogs({
                page,
                per_page: 10,
                search: searchQuery || undefined,
                event: filterEvent || undefined,
                subject_type: filterModule || undefined,
                all_sucursals: showAllSucursals,
            })

            // El backend devuelve ApiResponse::success(paginator)
            // → response.data = { data: { current_page, data: [...], total, ... } }
            const paginator = res?.data

            if (paginator?.data) {
                setLogs(paginator.data)
                setPagination({
                    currentPage: paginator.current_page,
                    total: paginator.total,
                    perPage: paginator.per_page,
                    lastPage: paginator.last_page,
                    from: paginator.from ?? 0,
                    to: paginator.to ?? 0,
                })
            } else {
                setLogs([])
                setPagination(INITIAL_PAGINATION)
            }
        } catch {
            toast.error('Error al cargar los registros de acciones')
        } finally {
            setIsLoading(false)
        }
    }, [searchQuery, filterEvent, filterModule, showAllSucursals])

    // Re-fetch cuando cambian filtros
    useEffect(() => {
        fetchLogs(1)
    }, [fetchLogs])

    // ─── Cambio de página ───
    const handlePageChange = (page: number) => {
        fetchLogs(page)
    }

    // ─── Renderizado ─────────────────────────────────────────────────────────
    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-slate-900">Registro de Acciones</h1>
                    <p className="mt-1 text-sm text-slate-500">
                        Historial de cambios realizados por los usuarios del sistema.
                    </p>
                </div>

                {/* Filtros */}
                <div className="flex flex-wrap items-center gap-2">
                    {/* Búsqueda */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <Input
                            placeholder="Buscar usuario, acción..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-56 pl-9"
                        />
                    </div>

                    {/* Tipo de evento */}
                    <select
                        value={filterEvent}
                        onChange={(e) => setFilterEvent(e.target.value)}
                        className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Todos los eventos</option>
                        <option value="created">Creación</option>
                        <option value="updated">Edición</option>
                        <option value="deleted">Eliminación</option>
                        <option value="role_permissions_synced">Permisos</option>
                        <option value="user_sucursals_synced">Sucursales</option>
                        <option value="status_changed">Cambio de estado</option>
                    </select>

                    {/* Módulo */}
                    <select
                        value={filterModule}
                        onChange={(e) => setFilterModule(e.target.value)}
                        className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Todos los módulos</option>
                        {Object.entries(MODULE_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>

                    {/* Todas las sucursales */}
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                        <input
                            type="checkbox"
                            checked={showAllSucursals}
                            onChange={(e) => setShowAllSucursals(e.target.checked)}
                            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        Todas las sucursales
                    </label>
                </div>
            </div>

            {/* Tabla */}
            {isLoading ? (
                <TableSkeleton
                    headers={['Usuario', 'Tipo', 'Descripción', 'Módulo', 'Sucursal', 'Fecha', '']}
                    columns={['text', 'badge', 'text', 'badge', 'text', 'text', 'text']}
                    rows={8}
                />
            ) : (
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full whitespace-nowrap">
                            <thead className="border-b border-slate-200 bg-slate-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Usuario</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Tipo</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Descripción</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Módulo</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Sucursal</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Fecha</th>
                                    <th className="px-4 py-3" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {logs.length > 0 ? (
                                    logs.map((log) => {
                                        const eventCfg = getEventConfig(log.event)
                                        const sevCfg = SEVERITY_CONFIG[eventCfg.severity]
                                        return (
                                            <tr
                                                key={log.id}
                                                className="group transition-colors hover:bg-slate-50"
                                            >
                                                {/* Usuario */}
                                                <td className="px-4 py-3">
                                                    <p className="font-medium text-slate-900">{log.causer_name || 'Sistema'}</p>
                                                    <p className="text-xs text-slate-400">{log.causer_email}</p>
                                                </td>

                                                {/* Tipo de evento */}
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${eventCfg.bgColor} ${eventCfg.textColor}`}>
                                                        <eventCfg.Icon className="h-3 w-3" />
                                                        {eventCfg.label}
                                                    </span>
                                                    {eventCfg.severity !== 'info' && (
                                                        <span className={`ml-1 inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${sevCfg.className}`}>
                                                            <sevCfg.Icon className="h-2.5 w-2.5" />
                                                            {sevCfg.label}
                                                        </span>
                                                    )}
                                                </td>

                                                {/* Descripción legible */}
                                                <td className="max-w-xs px-4 py-3">
                                                    <p className="truncate text-sm text-slate-700" title={buildMessage(log)}>
                                                        {buildMessage(log)}
                                                    </p>
                                                </td>

                                                {/* Módulo */}
                                                <td className="px-4 py-3">
                                                    <span className="inline-flex rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                                                        {getModuleLabel(log.subject_type)}
                                                    </span>
                                                </td>

                                                {/* Sucursal */}
                                                <td className="px-4 py-3 text-sm text-slate-600">
                                                    {log.sucursal_name || <span className="text-slate-300">—</span>}
                                                </td>

                                                {/* Fecha */}
                                                <td className="px-4 py-3 text-sm text-slate-500">
                                                    {formatDate(log.created_at)}
                                                </td>

                                                {/* Acción */}
                                                <td className="px-4 py-3">
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => setSelectedLog(log)}
                                                        className="h-7 w-7 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                                                        title="Ver detalle"
                                                    >
                                                        <Eye className="h-4 w-4 text-slate-500" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        )
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-16 text-center">
                                            <Package className="mx-auto mb-3 h-10 w-10 text-slate-200" />
                                            <p className="text-sm font-medium text-slate-500">No se encontraron registros</p>
                                            <p className="mt-1 text-xs text-slate-400">Intenta ajustar los filtros de búsqueda</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Paginación real desde backend */}
                    {pagination.total > 0 && (
                        <GlobalPaginator
                            totalItems={pagination.total}
                            itemsPerPage={pagination.perPage}
                            currentPage={pagination.currentPage}
                            from={pagination.from}
                            to={pagination.to}
                            onPageChange={handlePageChange}
                        />
                    )}
                </div>
            )}

            {/* Modal de detalle */}
            <ActivityDetailModal
                log={selectedLog}
                onClose={() => setSelectedLog(null)}
            />
        </div>
    )
}
